import { useEffect, useRef } from 'react';

interface Props {
  /** Сторона квадрата в пикселях CSS. */
  size?: number;
}

/**
 * Молекула дофамина из частиц — заставка онбординга (решение Славы 21.09.2026).
 *
 * Формула настоящая, а не «что-то химическое»: 4-(2-аминоэтил)бензол-1,2-диол.
 * Бензольное кольцо, две гидроксильные группы на соседних атомах (катехол)
 * и этиламиновый хвост с азотом на противоположной стороне кольца.
 * Кто разбирается в химии, узнает её сразу, и это работает на доверие.
 *
 * Сюжет анимации: частицы стартуют рассыпанной пылью и за ~1.6 с собираются
 * в молекулу, дальше она спокойно дышит и чуть покачивается.
 *
 * Бережём телефон так же, как в ParticleRing: пауза при скрытой вкладке,
 * без анимации при prefers-reduced-motion (сразу собранная молекула),
 * плотность пикселей не выше двух.
 */

type P = { x: number; y: number };

// Скелет в условных единицах: длина связи = 1.
function dopamineSkeleton() {
  const ring: P[] = [];
  for (let k = 0; k < 6; k++) {
    const a = ((90 - 60 * k) * Math.PI) / 180;
    ring.push({ x: Math.cos(a), y: -Math.sin(a) });
  }
  const out = (v: P, deg: number): P => {
    const a = (deg * Math.PI) / 180;
    return { x: v.x + Math.cos(a), y: v.y - Math.sin(a) };
  };
  // Гидроксилы на соседних атомах слева (k4, k5), хвост на k1 — напротив k4.
  const o1 = out(ring[4], -150);
  const o2 = out(ring[5], 150);
  const c7 = out(ring[1], 30);
  const c8 = out(c7, -30);
  const n = out(c8, 30);

  const bonds: [P, P][] = [];
  for (let k = 0; k < 6; k++) bonds.push([ring[k], ring[(k + 1) % 6]]);
  bonds.push([ring[4], o1], [ring[5], o2], [ring[1], c7], [c7, c8], [c8, n]);

  // Двойные связи ароматического кольца: внутренняя линия на трёх рёбрах через одно.
  const inner: [P, P][] = [];
  for (const k of [0, 2, 4]) {
    const a = ring[k];
    const b = ring[(k + 1) % 6];
    const s = 0.78;
    const t = 0.16;
    inner.push([
      { x: a.x * s + (b.x - a.x) * t, y: a.y * s + (b.y - a.y) * t },
      { x: b.x * s + (a.x - b.x) * t, y: b.y * s + (a.y - b.y) * t },
    ]);
  }

  const carbons = [...ring, c7, c8];
  const hetero = [o1, o2, n];
  return { bonds, inner, carbons, hetero };
}

export function ParticleMolecule({ size = 300 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const { bonds, inner, carbons, hetero } = dopamineSkeleton();

    // Вписываем скелет в квадрат с полями.
    const pts = [...carbons, ...hetero];
    const minX = Math.min(...pts.map((p) => p.x));
    const maxX = Math.max(...pts.map((p) => p.x));
    const minY = Math.min(...pts.map((p) => p.y));
    const maxY = Math.max(...pts.map((p) => p.y));
    const scale = (size * 0.78) / Math.max(maxX - minX, maxY - minY);
    const ox = size / 2 - ((minX + maxX) / 2) * scale;
    const oy = size / 2 - ((minY + maxY) / 2) * scale;
    const map = (p: P): P => ({ x: ox + p.x * scale, y: oy + p.y * scale });

    type Dot = { hx: number; hy: number; sx: number; sy: number; r: number; phase: number; bright: number };
    const dots: Dot[] = [];
    const gauss = () => (Math.random() + Math.random() + Math.random() - 1.5) / 1.5;
    const spawn = (x: number, y: number, r: number, bright: number) => {
      const a = Math.random() * Math.PI * 2;
      const d = size * (0.35 + Math.random() * 0.4);
      dots.push({
        hx: x, hy: y,
        sx: size / 2 + Math.cos(a) * d, sy: size / 2 + Math.sin(a) * d,
        r, phase: Math.random() * Math.PI * 2, bright,
      });
    };

    // Ореол рисуется ПЕРВЫМ слоем, под молекулой: крупные полупрозрачные точки
    // у атомов дают объёмное свечение, а яркие частицы ложатся сверху.
    // без дорогого размытия на каждой частице (shadowBlur на 2000 точках
    // заметно просаживает кадры на слабых телефонах).
    for (const a of [...carbons, ...hetero]) {
      const A = map(a);
      const heavy = hetero.includes(a);
      for (let i = 0; i < (heavy ? 26 : 10); i++) {
        spawn(A.x + gauss() * size * (heavy ? 0.07 : 0.04), A.y + gauss() * size * (heavy ? 0.07 : 0.04), heavy ? 4.5 : 3, 0.12);
      }
    }

    const perBond = Math.round(size * 0.34);
    for (const [a, b] of bonds) {
      const A = map(a); const B = map(b);
      const len = Math.hypot(B.x - A.x, B.y - A.y);
      const nx = -(B.y - A.y) / len; const ny = (B.x - A.x) / len;
      for (let i = 0; i < perBond; i++) {
        const t = Math.random();
        const j = gauss() * size * 0.02;
        spawn(A.x + (B.x - A.x) * t + nx * j, A.y + (B.y - A.y) * t + ny * j, Math.random() < 0.8 ? 1 : 1.6, 0.75);
      }
    }
    for (const [a, b] of inner) {
      const A = map(a); const B = map(b);
      for (let i = 0; i < perBond * 0.6; i++) {
        const t = Math.random();
        spawn(A.x + (B.x - A.x) * t, A.y + (B.y - A.y) * t, 1.1, 0.7);
      }
    }
    for (const c of carbons) {
      const C = map(c);
      for (let i = 0; i < size * 0.05; i++) spawn(C.x + gauss() * size * 0.018, C.y + gauss() * size * 0.018, 1.2, 0.9);
    }
    // Кислород и азот — ярче и плотнее: глаз сразу находит «концы» молекулы.
    for (const h of hetero) {
      const H = map(h);
      for (let i = 0; i < size * 0.22; i++) spawn(H.x + gauss() * size * 0.04, H.y + gauss() * size * 0.04, Math.random() < 0.6 ? 1.4 : 2.4, 1);
    }
    // Лёгкая пыль вокруг, чтобы молекула не висела в вакууме.
    for (let i = 0; i < size * 0.35; i++) {
      spawn(size / 2 + gauss() * size * 0.45, size / 2 + gauss() * size * 0.45, 0.8, 0.25);
    }

    const ASSEMBLE_MS = 1600;
    let start = 0;
    let raf = 0;
    let running = true;
    const ease = (x: number) => 1 - Math.pow(1 - x, 3);

    function frame(now: number) {
      if (!ctx) return;
      if (!start) start = now;
      const t = now - start;
      const k = reduced ? 1 : ease(Math.min(1, t / ASSEMBLE_MS));
      // После сборки — лёгкое покачивание и дыхание всей молекулы.
      const sway = reduced ? 0 : Math.sin(t * 0.0005) * 0.06 * k;
      const breathe = reduced ? 1 : 1 + Math.sin(t * 0.0011) * 0.015 * k;
      const cs = Math.cos(sway); const sn = Math.sin(sway);
      const c = size / 2;

      ctx.clearRect(0, 0, size, size);
      ctx.fillStyle = '#6fcb9a';
      for (const d of dots) {
        const jitter = reduced ? 0 : Math.sin(t * 0.002 + d.phase) * 0.8;
        let x = d.sx + (d.hx - d.sx) * k + jitter;
        let y = d.sy + (d.hy - d.sy) * k + Math.cos(t * 0.0017 + d.phase) * (reduced ? 0 : 0.8);
        const dx = (x - c) * breathe; const dy = (y - c) * breathe;
        x = c + dx * cs - dy * sn;
        y = c + dx * sn + dy * cs;
        const twinkle = reduced ? 1 : 0.6 + 0.4 * Math.sin(t * 0.0013 + d.phase * 2);
        ctx.globalAlpha = Math.min(1, d.bright * twinkle * (0.35 + 0.65 * k));
        ctx.beginPath();
        ctx.arc(x, y, d.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      if (running && !reduced) raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);

    function onVisibility() {
      if (document.visibilityState === 'hidden') {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        raf = requestAnimationFrame(frame);
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      running = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [size]);

  return <canvas ref={canvasRef} style={{ width: size, height: size }} aria-hidden="true" />;
}
